import React from "react";
import PropTypes from "prop-types";
import { Col, Form, Row } from "reactstrap";
import { MIXED } from "../../shared/bulkFields";
import { Section } from "./FieldRow";
import useBulkForm from "./useBulkForm";
import {
  BulkFieldRow,
  BulkFormHeader,
  BulkFormSummary,
  BulkSelect,
  BulkTriBool,
  TriStateCheckbox,
  VARIES_LABEL,
} from "./bulkControls";

const ALERT_FLAGS = [
  "body_theft",
  "gesture_into_body",
  "product_in_personal_bag",
  "product_into_backpack",
  "suspicious_bag",
  "product_into_stroller",
  "client_normal_bag",
  "store_normal_bag",
  "deblistering",
  "consumption",
  "burst_shot",
  "other_suspicious",
];

const sensitivityLabel = (s) => `Level ${s.level}${s.name ? ` — ${s.name}` : ""}`;

function fkOptions(options, getLabel) {
  return (options || []).map((option) => ({ value: option.id, label: getLabel(option) }));
}

/**
 * Edits the bulk-shared view fields across a selection: the twelve alert flags,
 * the inference sensitivities and relevance, and the active state.
 *
 * The polygon (view_area) and the identity fields (view_number, view_name) are
 * per-view by nature and stay in the single-view form.
 */
export default function BulkEditViewForm({ views, serverViews, foreignKeys, dispatch }) {
  const form = useBulkForm(views, serverViews, "view", dispatch);

  const fkRow = (field, label, options, getLabel, emptyLabel) => (
    <BulkFieldRow
      selectedCount={views.length}
      label={label}
      modified={form.isModified(field)}
      onRevert={form.revertField(field)}
    >
      <BulkSelect
        label={label}
        value={form.valueOf(field)}
        options={fkOptions(options, getLabel)}
        onChange={form.setField(field)}
        allowEmpty
        emptyLabel={emptyLabel}
      />
    </BulkFieldRow>
  );

  return (
    <Form>
      <BulkFormHeader count={views.length} noun={views.length === 1 ? "view" : "views"} />

      <Section title="Inference">
        {fkRow("body_sensitivity", "Body sensitivity", foreignKeys.bodySensitivities, sensitivityLabel, "— default —")}
        {fkRow("bag_sensitivity", "Bag sensitivity", foreignKeys.bagSensitivities, sensitivityLabel, "— default —")}
        {fkRow("relevance", "Relevance", foreignKeys.relevances, (r) => r.name || `#${r.id}`, "— none —")}
      </Section>

      <Section title="State">
        <BulkFieldRow
          selectedCount={views.length}
          label="Active"
          modified={form.isModified("is_active")}
          onRevert={form.revertField("is_active")}
        >
          <TriStateCheckbox label="Active" value={form.valueOf("is_active")} onChange={form.setField("is_active")} />
        </BulkFieldRow>
      </Section>

      <Section title="Alert flags (12)">
        <Row>
          {ALERT_FLAGS.map((flag) => {
            const label = flag.replace(/_/g, " ");
            const value = form.valueOf(flag);
            return (
              <Col key={flag} md="6" className="mb-1">
                <div
                  className="d-flex align-items-center"
                  data-testid={`bulk-flag-${flag}`}
                  data-modified={form.isModified(flag) ? "true" : "false"}
                  style={{
                    gap: 6,
                    background: form.isModified(flag) ? "rgba(74, 134, 255, 0.10)" : undefined,
                    borderRadius: 4,
                  }}
                >
                  <div style={{ fontSize: "0.8rem", flex: 1 }}>
                    {label}
                    {value === MIXED && (
                      <span className="text-muted font-italic" style={{ fontSize: "0.7rem" }}>
                        {" "}
                        {VARIES_LABEL}
                      </span>
                    )}
                  </div>
                  <div style={{ width: 130 }}>
                    <BulkTriBool label={label} value={value} onChange={form.setField(flag)} />
                  </div>
                  {form.isModified(flag) && (
                    <button
                      type="button"
                      className="close"
                      aria-label={`Revert ${label}`}
                      title={`Revert this field on all ${views.length} selected, to each one's saved value`}
                      onClick={form.revertField(flag)}
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  )}
                </div>
              </Col>
            );
          })}
        </Row>
      </Section>

      <BulkFormSummary dirtyCount={form.dirtyCount} count={views.length} />
    </Form>
  );
}

BulkEditViewForm.propTypes = {
  views: PropTypes.arrayOf(PropTypes.shape({ public_id: PropTypes.string.isRequired })).isRequired,
  serverViews: PropTypes.arrayOf(PropTypes.shape({ public_id: PropTypes.string })).isRequired,
  foreignKeys: PropTypes.shape({
    bodySensitivities: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
    bagSensitivities: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
    relevances: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
};
